import { HttpHeaders, HttpRequest } from 'core/classes/http'
import Logger from 'core/utils/Logger'
import settings from 'src/config/settings'

/**
 * The rid-keyed reads the legacy registration wizards used to make against App Designer's
 * `data-set-provider` with a filter built here in the browser. The service now owns the query and
 * checks, per request, that the caller is recorded as its requestor.
 *
 * @deprecated PFP_LEGACY_RID — dies with the legacy wizards. New code uses the uid-keyed
 * `/api/v0/pfp/partner-registration/:requestUid` and `/bp-forms-step-mapping-v2/:requestUid`.
 * The service-side removal list is in `app/modules/pfp-form/legacy/deprecation.ts`.
 */

const LEGACY_RID_BASE = () => `${settings.httpsServices.onboarding}/api/v0/pfp/legacy-rid`

/**
 * The app's shared transport, the same one the `data-set-provider` reads went through before they
 * were moved here. It owns two things this module must not own a second copy of: the
 * `Authorization` header — set only when a token exists — and the single-flight
 * refresh-and-replay on 401, which these forms need because they are filled over tens of minutes.
 *
 * `HttpRequest` directly and not `Rest`, which is otherwise the more common wrapper: `Rest` throws
 * when the response carries an error, and a throw is the one thing a caller here cannot survive.
 */
const request = new HttpRequest(new HttpHeaders())

/**
 * How long a read is given before it is abandoned. Not a promise about elapsed time: the
 * transport's retry sleeps do not observe the signal, so an abort landing inside one is discovered
 * only when it ends — up to eight seconds later on a 429.
 *
 * Nothing else bounds this at all. The shared axios instance is built without a `timeout`, so a socket that
 * is opened and never answered — a hung proxy — leaves the promise pending forever, and a pending
 * promise is what the wizards cannot survive: they turn their spinner off on the line after they
 * await this. The transport forwards this signal into its own 401-replay and 429-retry, so the
 * budget covers the whole attempt chain rather than each attempt.
 *
 * Built from `AbortController` rather than `AbortSignal.timeout`, which needs Safari 15.4 while
 * this app still declares `safari > 13` / `ios_saf > 13` and this is a page external partners land
 * on. A missing method would throw here and produce the exact hang it was added to prevent.
 */
const READ_TIMEOUT_MS = 30_000

/** JSend, the envelope every `pfp` route answers. The `error` string a failing route sends with it
 *  is unreachable from here — see `ITransportResult`. */
interface IJSend<T> {
  status?: 'success' | 'fail' | 'error'
  data?: T
}

/**
 * What the shared transport hands back:
 *
 *  - on success, the axios response — `status` 200 and the JSend envelope under `data`;
 *  - on a status it did not like, a stand-in carrying that status and, under `data.Errors`, axios's
 *    own message. The service's JSend `error` string does not survive this: the transport rebuilds
 *    the body and looks for a `Message` field, which these routes do not send;
 *  - on our abort, `{ cancelled: true }` and nothing else;
 *  - on a request it never sent at all — a rejected local token, a failed refresh — a bare `{}`.
 */
interface ITransportResult<T> {
  status?: number
  cancelled?: boolean
  data?: Partial<IJSend<T>> & { Errors?: string }
}

/**
 * What a read came back as. Each of the four gets a different screen in the wizards, which is why
 * the status leaves this module as a value instead of collapsing into `null`:
 *
 *  - `ok` — the row, or the step map.
 *  - `refused` — 403. Either the request is not this caller's, or there is no such request: the
 *    gate answers both alike on purpose, so probing sequential ids teaches nothing. Neither this
 *    module nor the screen above it can tell the two apart.
 *  - `gone` — 404, or a success envelope with no row in it. The caller may read this request; there
 *    is nothing to read.
 *  - `unavailable` — the service did not answer: 503 while it could not decide ownership, our own
 *    timeout, an offline browser, a 400 on a malformed id, a 500. **Not** a refusal — nothing may
 *    log the caller out or tell them they have no access on the strength of it.
 */
export type TLegacyRidFailure = 'refused' | 'gone' | 'unavailable'

export type TLegacyRidRead<T> = { outcome: 'ok'; data: T } | { outcome: TLegacyRidFailure }

/**
 * The rows, or a stand-in — for a caller that has no screen for a read that failed, and would
 * render the stand-in anyway. It **discards the outcome**, so a refusal and an outage become
 * indistinguishable from a legitimately empty answer.
 *
 * Grep this name to find those callers — and `outcome === 'ok'` to find the rest, because a caller
 * that needs the row itself rather than a fallback unwraps it with a plain guard instead. The two
 * live wizards and the terms-and-conditions page discard nothing, and must not.
 */
export const readOr = <T>(result: TLegacyRidRead<T>, fallback: T): T =>
  result.outcome === 'ok' ? result.data : fallback

/** Before either default below is applied: `ok` with no row is still an open question. */
type TRawRead<T> = { outcome: 'ok'; data?: T } | { outcome: TLegacyRidFailure }

/**
 * A row read. A success envelope carrying no row means what the service's own 404 means — this
 * request has no row in the view — and the wizards render the two the same way.
 */
function requireRow<T>(result: TRawRead<T>): TLegacyRidRead<T> {
  if (result.outcome !== 'ok') {
    return result
  }

  // `== null` on purpose: an explicit `data: null` is as much "no row" as an absent field.
  return result.data == null ? { outcome: 'gone' } : { outcome: 'ok', data: result.data }
}

/** A step-map read. A success envelope carrying no rows is a request that has no steps, which is a
 *  legitimate answer the wizard branches on — not an absence. */
function defaultToEmpty<T>(result: TRawRead<T[]>): TLegacyRidRead<T[]> {
  return result.outcome === 'ok' ? { outcome: 'ok', data: result.data ?? [] } : result
}

/**
 * The statuses behind those outcomes:
 *
 *  - 200 — `ok`.
 *  - 403 — `refused`; 404 — `gone`.
 *  - 503, 400, 500, a timeout, no answer at all — `unavailable`.
 *
 * Never rejects. A wizard awaits this inside its own `init()` and turns its spinner off on the
 * next line, and `src/modules` is outside this repo's test surface — so the transport's own
 * no-reject property is a guarantee no test here holds, and the promise is closed off in this
 * module instead of trusted from below.
 *
 * The timer bounds the request itself and the transport's retry chain. It does not bound the two
 * awaits the transport takes before axios, neither of which observes the signal: its
 * identical-request queue, which is bounded in practice, and its cross-request refresh `blocker`,
 * which is not — a refresh that ends with no token leaves that promise unresolved forever. What
 * covers *that* case is not this timer: the transport notifies a 401 on its way past, and the app
 * navigates to the login screen.
 */
async function read<T>(path: string): Promise<TRawRead<T>> {
  const abort = new AbortController()
  const timer = setTimeout(() => abort.abort(), READ_TIMEOUT_MS)

  let response: ITransportResult<T> | undefined
  try {
    response = await request.get({ url: `${LEGACY_RID_BASE()}${path}`, signal: abort.signal })
  } catch (e) {
    Logger.error(`[PFP_LEGACY_RID] ${path}: the read threw — ${String(e)}`)
    return { outcome: 'unavailable' }
  } finally {
    clearTimeout(timer)
  }

  const { status, cancelled, data: body } = response ?? {}

  if (status === 200 && body?.status === 'success') {
    return { outcome: 'ok', data: body.data }
  }

  if (status === 403) {
    return { outcome: 'refused' }
  }

  if (status === 404) {
    return { outcome: 'gone' }
  }

  // Everything from here is `unavailable`, and logged apart so the console says which kind. The
  // outcome the caller gets is deliberately coarser than these lines: the wizards render one
  // "try again later" screen for all of them.
  if (status === 503) {
    Logger.error(`[PFP_LEGACY_RID] ${path}: the service could not decide ownership (503)`)
  } else if (cancelled) {
    // Ours: this module is the only thing that cancels these reads.
    Logger.error(`[PFP_LEGACY_RID] ${path}: gave up after ${READ_TIMEOUT_MS}ms`)
  } else if (status === undefined) {
    // Either the call got no answer — offline, DNS, CORS — or the transport refused to send it,
    // which it does after rejecting the stored token, and which logs out on its way past.
    Logger.error(
      `[PFP_LEGACY_RID] ${path}: no answer, or the request was never sent — ${
        body?.Errors ?? 'see the transport log above'
      }`
    )
  } else if (status === 200) {
    // The route answered, and said no. Nothing sends this today; a status-only check would have
    // taken it for a row.
    Logger.error(`[PFP_LEGACY_RID] ${path}: the envelope reports ${body?.status ?? 'no status'}`)
  } else {
    Logger.error(`[PFP_LEGACY_RID] ${path}: unexpected ${status} ${body?.Errors ?? ''}`)
  }

  return { outcome: 'unavailable' }
}

class LegacyRidApi {
  /**
   * The request row. Which of the two views backs it is decided by the service from the request's
   * own type — the caller no longer names it.
   */
  public async fetchRequestData<T>(requestId: number | string): Promise<TLegacyRidRead<T>> {
    return requireRow(await read<T>(`/request-data/${requestId}`))
  }

  /** The step→action map, legacy form-996 Version 1. Unbounded: the 50-row page size the CrudStore
   *  path asked App Designer for went with it, and the service's `findAllByRequestId` sets none. */
  public async fetchStepMapping<T>(requestId: number | string): Promise<TLegacyRidRead<T[]>> {
    return defaultToEmpty(await read<T[]>(`/step-mapping/${requestId}`))
  }

  /** Four fields for the terms-and-conditions page. `Data` arrives exactly as the view holds it,
   *  null included — the page decides the right to sign from `parseJson(Data).Email`. */
  public async fetchPartnerRegistration<T>(requestId: number | string): Promise<TLegacyRidRead<T>> {
    return requireRow(await read<T>(`/partner-registration/${requestId}`))
  }
}

export const legacyRidApi = new LegacyRidApi()
